import { PhxCalendarConfiguration } from './../../config/calendar_configuration';
import { ITimeOccurrenceDatatype, IRawTimeOccurrenceDatatype } from '../../datastore/time/data_types';
import { TimeOccurrenceEngine } from '../../engines/time/time_engine';
import { TimeOccurrenceDataSource } from '../../datastore/time/time_datasource';
import { IWeekCell } from '../../views/week_calendar_view/data_types';
import { Duration } from '../data_types';
import { IDayOfTheWeekValue, IDayOfTheWeekEnum } from 'utility-js-time';
import * as _4 from 'lodash4';

export class WeekCalendarCellServant {
  constructor(
    private ngTimeOccurrenceEngine: TimeOccurrenceEngine,
    private DayOfTheWeekEnum: IDayOfTheWeekEnum,
    private ngTimeOccurrenceDataSource: TimeOccurrenceDataSource,
    private ngPhxCalendarConfiguration: PhxCalendarConfiguration
  ) {}

  createTimeOccurrenceForCell(cell: IWeekCell): ITimeOccurrenceDatatype {
    const rawTime: IRawTimeOccurrenceDatatype = this.createRawTimeOccurrenceForCell(cell);
    return this.ngTimeOccurrenceDataSource.buildTime(rawTime);
  }

  createRawTimeOccurrenceForCell(cell: IWeekCell): IRawTimeOccurrenceDatatype {
    const to_hour = cell.minutes === '45' ? parseInt(cell.hour, 10) + 1 : cell.hour;
    const to_minutes = cell.minutes === '45' ? '00' : parseInt(cell.minutes, 10) + 15;

    return {
      day_of_week: this.DayOfTheWeekEnum.get(cell.day)!.ordinal,
      from_tm: cell.hour + ':' + _.padLeft(cell.minutes, 2, '0'),
      to_tm: to_hour + ':' + to_minutes + ':00'
    };
  }

  isCellOpen(cell: IWeekCell, timeOccurrences: ITimeOccurrenceDatatype[]): boolean {
    return !this.findTimeOccurrenceForCell(cell, timeOccurrences);
  }

  findTimeOccurrenceForCell(cell: IWeekCell, timeOccurrences: ITimeOccurrenceDatatype[]): ITimeOccurrenceDatatype {
    const builtCell: ITimeOccurrenceDatatype = this.createTimeOccurrenceForCell(cell);
    return _.find(
      timeOccurrences,
      (timeOccurrence: ITimeOccurrenceDatatype): boolean => {
        return this.ngTimeOccurrenceEngine.areTimesPartiallyOverlapping(timeOccurrence, builtCell);
      },
      {}
    );
  }

  createTimeOccurrenceFromCellAndExistingTime(
    cell: IWeekCell,
    timeOccurrence: ITimeOccurrenceDatatype,
    originalTimeOccurrence: ITimeOccurrenceDatatype,
    deletedTimeOccurrence: ITimeOccurrenceDatatype
  ): IRawTimeOccurrenceDatatype {
    const duration: Duration = timeOccurrence.to.diff(timeOccurrence.from);

    const cellWithDragOffset = calculateCellOffset(cell, originalTimeOccurrence, deletedTimeOccurrence);

    const cellTime: ITimeOccurrenceDatatype = this.createTimeOccurrenceForCell(cellWithDragOffset);
    let cellRawTime: IRawTimeOccurrenceDatatype = this.ngTimeOccurrenceDataSource.serializeTime(cellTime);
    const originalRawTime: IRawTimeOccurrenceDatatype = this.ngTimeOccurrenceDataSource.serializeTime(timeOccurrence);
    const savedCellStartTime: string | null = cellRawTime.from_tm;
    const savedCellDay: number = cellRawTime.day_of_week;

    _.forEach(originalRawTime, function (value: any, key: string): void {
      cellRawTime[key] = value;
    });

    cellRawTime.from_tm = savedCellStartTime;
    cellRawTime.day_of_week = savedCellDay;
    let newTimeOccurrence: ITimeOccurrenceDatatype = this.ngTimeOccurrenceDataSource.buildTime(cellRawTime);

    newTimeOccurrence.to = moment(newTimeOccurrence.from);
    newTimeOccurrence.to.add(duration, 'milliseconds');
    const hourBeginMoment = moment({ hour: _4.get(this.ngPhxCalendarConfiguration, 'hourBegin', 7), minutes: 0 });
    const hourEndMoment = moment({ hour: _4.get(this.ngPhxCalendarConfiguration, 'hourEnd', 22), minutes: 59 });
    if (hourBeginMoment.isAfter(newTimeOccurrence.from)) {
      newTimeOccurrence.from = hourBeginMoment;
      newTimeOccurrence.to = moment(hourBeginMoment).add(duration, 'milliseconds');
    }
    if (hourEndMoment.isBefore(newTimeOccurrence.to)) {
      newTimeOccurrence.to = hourEndMoment;
      newTimeOccurrence.from = moment(hourEndMoment).subtract(duration, 'milliseconds');
    }
    return this.ngTimeOccurrenceDataSource.serializeTime(newTimeOccurrence);

    function calculateCellOffset(
      cellObj: IWeekCell,
      originalTime: ITimeOccurrenceDatatype,
      deletedTime: ITimeOccurrenceDatatype
    ): IWeekCell {
      const cellTimeInput = { hour: parseInt(cellObj.hour, 10), minute: parseInt(cellObj.minutes, 10) };
      let cellTimeMoment: moment.Moment = moment(cellTimeInput);
      let deletedTimeFrom = moment(deletedTime.fromInput, 'hh:mm');

      const hourOffsetByAreaClicked: number = getHourOffsetByDragArea(originalTime, deletedTimeFrom);
      let offsetHourByArea = parseInt(cellObj.hour, 10) - hourOffsetByAreaClicked;

      cellObj.hour = offsetHourByArea.toString();

      return cellObj;

      function getHourOffsetByDragArea(origTime: ITimeOccurrenceDatatype, delTimeFrom: moment.Moment): number {
        let draggedCellMovingTimeDiffMins: number = cellTimeMoment.diff(delTimeFrom, 'minutes');
        draggedCellMovingTimeDiffMins = Math.abs(draggedCellMovingTimeDiffMins);

        const originalTimeDuration: Duration = origTime.to.diff(origTime.from, 'minutes');
        const durationThird = originalTimeDuration / 3;
        const topRange = [0, durationThird];
        const midRange = [durationThird + 1, durationThird * 2];
        const lowRange = [durationThird * 2 + 1, durationThird * 3];
        let selectedRange = 0;
        let rangeClicked = {
          top: false,
          mid: false,
          low: false
        };

        if (_.inRange(draggedCellMovingTimeDiffMins, topRange[0], topRange[1])) {
          selectedRange = durationThird / 2;
          rangeClicked.top = true;
        } else if (_.inRange(draggedCellMovingTimeDiffMins, midRange[0], midRange[1])) {
          selectedRange = durationThird + durationThird / 2;
          rangeClicked.mid = true;
        } else {
          selectedRange = durationThird * 2 + durationThird / 2;
          rangeClicked.low = true;
        }

        let hourOffsetByRange = Math.floor(selectedRange / 60);
        if (_.inRange(originalTimeDuration, 130, 240)) {
          if (rangeClicked.low || rangeClicked.mid) {
            hourOffsetByRange = 2;
          } else if (rangeClicked.top) {
            hourOffsetByRange = 0;
          }
        }

        return hourOffsetByRange;
      }
    }
  }

  createCellFromEvent(event: Event): IWeekCell | undefined {
    const target = <HTMLElement>event.target;
    if (!target.hasAttribute('data-day')) {
      return;
    }
    return {
      day: target.attributes['data-day'].value,
      hour: target.attributes['data-hour'].value,
      minutes: target.attributes['data-minutes'].value
    };
  }
}

angular
  .module('ui-ng-calendar')
  .factory(
    'ngWeekCalendarCellServant',
    function (
      ngTimeOccurrenceEngine: any,
      DayOfTheWeekEnum: IDayOfTheWeekEnum,
      ngTimeOccurrenceDataSource: TimeOccurrenceDataSource,
      ngPhxCalendarConfiguration: PhxCalendarConfiguration
    ): any {
      return new WeekCalendarCellServant(
        ngTimeOccurrenceEngine,
        DayOfTheWeekEnum,
        ngTimeOccurrenceDataSource,
        ngPhxCalendarConfiguration
      );
    }
  );



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-calendar/src/servants/week_calendar/week_calendar_servant.ts